import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Store, Check, X, Trash2, Star, StarOff, Eye, Globe, Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBusinesses() {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const response = await axios.get(`${API}/features/local-businesses?approved_only=false`, { withCredentials: true });
      setBusinesses(response.data);
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/features/local-businesses/${id}`, { 
        status,
        admin_notes: adminNotes 
      }, { withCredentials: true });
      toast.success(`Business ${status}`);
      setSelectedBusiness(null);
      setAdminNotes('');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const toggleFeatured = async (id, currentFeatured) => {
    try {
      await axios.put(`${API}/features/local-businesses/${id}`, { 
        is_featured: !currentFeatured 
      }, { withCredentials: true });
      toast.success(currentFeatured ? 'Removed from featured' : 'Added to featured');
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const deleteBusiness = async (id) => {
    if (!window.confirm('Delete this business listing?')) return;
    try {
      await axios.delete(`${API}/features/local-businesses/${id}`, { withCredentials: true });
      toast.success('Business deleted');
      if (selectedBusiness?.id === id) setSelectedBusiness(null);
      fetchBusinesses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredBusinesses = businesses.filter(b => {
    if (filter === 'all') return true;
    return b.status === filter;
  });

  const statusColors = {
    pending: 'bg-gold/10 text-gold',
    approved: 'bg-olive/10 text-olive',
    denied: 'bg-red-100 text-red-600'
  };

  const pendingCount = businesses.filter(b => b.status === 'pending').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-businesses-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif text-charcoal">Local Businesses</h1>
          <p className="text-charcoal/60">
            {pendingCount > 0 ? `${pendingCount} pending review` : 'Manage business directory listings'}
          </p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ({businesses.length})</SelectItem>
            <SelectItem value="pending">Pending ({businesses.filter(b => b.status === 'pending').length})</SelectItem>
            <SelectItem value="approved">Approved ({businesses.filter(b => b.status === 'approved').length})</SelectItem>
            <SelectItem value="denied">Denied ({businesses.filter(b => b.status === 'denied').length})</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-gold">{businesses.filter(b => b.status === 'pending').length}</p>
          <p className="text-sm text-charcoal/60">Pending</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-olive">{businesses.filter(b => b.status === 'approved').length}</p>
          <p className="text-sm text-charcoal/60">Approved</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-red-500">{businesses.filter(b => b.status === 'denied').length}</p>
          <p className="text-sm text-charcoal/60">Denied</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gold/10">
          <p className="text-2xl font-serif text-burgundy">{businesses.filter(b => b.is_featured).length}</p>
          <p className="text-sm text-charcoal/60">Featured</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Business List */}
        <div className="space-y-4">
          {filteredBusinesses.map((business) => (
            <motion.div
              key={business.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => { setSelectedBusiness(business); setAdminNotes(business.admin_notes || ''); }}
              className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${
                selectedBusiness?.id === business.id 
                  ? 'border-burgundy shadow-lg' 
                  : 'border-gold/10 hover:border-gold/30'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  {business.logo ? (
                    <img src={business.logo} alt={business.business_name} className="w-12 h-12 object-contain rounded-lg bg-cream" />
                  ) : (
                    <div className="w-12 h-12 bg-purple/10 rounded-lg flex items-center justify-center">
                      <Store size={24} className="text-purple" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-charcoal">{business.business_name}</h3>
                      {business.is_featured && <Star size={14} className="text-gold fill-gold" />}
                    </div>
                    <p className="text-sm text-charcoal/50">{business.owner_name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[business.status]}`}>
                        {business.status}
                      </span>
                      <span className="text-xs text-charcoal/40">{business.category}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {business.status === 'approved' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFeatured(business.id, business.is_featured); }}
                      className={`p-2 ${business.is_featured ? 'text-gold' : 'text-charcoal/30 hover:text-gold'}`}
                      title={business.is_featured ? 'Remove from featured' : 'Add to featured'}
                    >
                      {business.is_featured ? <Star size={18} className="fill-gold" /> : <StarOff size={18} />}
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteBusiness(business.id); }}
                    className="p-2 text-charcoal/30 hover:text-red-500"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
          
          {filteredBusinesses.length === 0 && (
            <div className="text-center py-12 text-charcoal/50">
              <Store size={48} className="mx-auto mb-4 opacity-20" />
              <p>No businesses found</p>
            </div>
          )}
        </div>

        {/* Business Detail */}
        {selectedBusiness && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl p-6 border border-gold/10 h-fit sticky top-4"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {selectedBusiness.logo ? (
                  <img src={selectedBusiness.logo} alt="" className="w-16 h-16 object-contain rounded-xl bg-cream" />
                ) : (
                  <div className="w-16 h-16 bg-purple/10 rounded-xl flex items-center justify-center">
                    <Store size={32} className="text-purple" />
                  </div>
                )}
                <div>
                  <h2 className="font-serif text-xl text-charcoal">{selectedBusiness.business_name}</h2>
                  <p className="text-charcoal/60">{selectedBusiness.category}</p>
                </div>
              </div>
              <span className={`text-sm px-3 py-1 rounded-full ${statusColors[selectedBusiness.status]}`}>
                {selectedBusiness.status}
              </span>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 text-charcoal/70">
                <Mail size={16} />
                <a href={`mailto:${selectedBusiness.email}`} className="hover:text-burgundy">{selectedBusiness.email}</a>
              </div>
              {selectedBusiness.phone && (
                <div className="flex items-center gap-2 text-charcoal/70">
                  <Phone size={16} />
                  <span>{selectedBusiness.phone}</span>
                </div>
              )}
              {selectedBusiness.address && (
                <div className="flex items-center gap-2 text-charcoal/70">
                  <MapPin size={16} />
                  <span>{selectedBusiness.address}</span>
                </div>
              )}
              {selectedBusiness.website && (
                <div className="flex items-center gap-2">
                  <Globe size={16} className="text-charcoal/70" />
                  <a href={selectedBusiness.website} target="_blank" rel="noopener noreferrer" className="text-burgundy hover:underline">
                    {selectedBusiness.website}
                  </a>
                </div>
              )}
            </div>

            <div className="bg-cream/50 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-medium text-charcoal mb-2">Description</h4>
              <p className="text-charcoal/70 text-sm">{selectedBusiness.description}</p>
            </div>

            <div className="mb-6">
              <h4 className="text-sm font-medium text-charcoal mb-2">Admin Notes</h4>
              <Textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add internal notes about this business..."
                rows={3}
              />
            </div>

            {selectedBusiness.status === 'pending' && (
              <div className="flex gap-2">
                <Button
                  onClick={() => updateStatus(selectedBusiness.id, 'approved')}
                  className="flex-1 bg-olive hover:bg-olive/80"
                >
                  <Check size={18} className="mr-2" /> Approve
                </Button>
                <Button
                  onClick={() => updateStatus(selectedBusiness.id, 'denied')}
                  variant="outline"
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <X size={18} className="mr-2" /> Deny
                </Button>
              </div>
            )}

            {selectedBusiness.status === 'approved' && (
              <div className="flex gap-2">
                <Button
                  onClick={() => toggleFeatured(selectedBusiness.id, selectedBusiness.is_featured)}
                  variant="outline"
                  className="flex-1"
                >
                  {selectedBusiness.is_featured ? <StarOff size={18} className="mr-2" /> : <Star size={18} className="mr-2" />}
                  {selectedBusiness.is_featured ? 'Remove Featured' : 'Make Featured'}
                </Button>
                <Button
                  onClick={() => updateStatus(selectedBusiness.id, 'denied')}
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  <X size={18} className="mr-2" /> Revoke
                </Button>
              </div>
            )}

            {selectedBusiness.status === 'denied' && (
              <Button
                onClick={() => updateStatus(selectedBusiness.id, 'approved')}
                className="w-full bg-olive hover:bg-olive/80"
              >
                <Check size={18} className="mr-2" /> Approve
              </Button>
            )}

            <p className="text-xs text-charcoal/40 mt-4 text-center">
              Submitted: {new Date(selectedBusiness.created_at).toLocaleDateString()}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
